import { Component, OnInit, Input, SimpleChanges, OnChanges, ChangeDetectorRef, ViewChild, AfterViewInit, OnDestroy, ChangeDetectionStrategy, Output, EventEmitter } from '@angular/core';
import { IonSearchbar, ModalController } from '@ionic/angular';
import { MyApp } from 'src/app/api/helper/app';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Utils } from 'src/app/api/helper/utils';
import { AdresseListItem, VertragListItem } from 'src/app/api/model/model';
import { Adresse, Auftragstyp, Vertrag } from 'src/app/api/model/swagger-model';
import { RemoteService } from 'src/app/api/remote.service';
import { StammdatenService } from 'src/app/api/stammdaten.service';
import { SystemService } from 'src/app/api/system.service';
import { VertragDetailsComponent } from '../vertrag-details/vertrag-details.component';
import { AdresseDetailsComponent } from '../adresse-details/adresse-details.component';

const log = new Logger("AdresseSuchenComponent");

@Component({
    selector: 'app-adresse-suchen',
    templateUrl: './adresse-suchen.component.html',
    styleUrls: ['./adresse-suchen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdresseSuchenComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    static instance: AdresseSuchenComponent;

    static adresseCache: Adresse[] = null;

    /**
     * Wird gesetzt, sobald das ERP-System Entfernungen liefert, also der Umkreis-Filter aktiv ist.
     * Der Umkreis gilt nur für die Startliste ohne Suchtext, die Suche läuft über alle Adressen.
     * Steuert nur den Hinweistext bei leerer Liste.
     */
    static geoFilterAktiv = false;

    @ViewChild('searchbar', { static: false }) searchbar: IonSearchbar;

    @Input() auftragstyp: Auftragstyp;

    items: AdresseListItem[];

    sichtbareItems: AdresseListItem[];

    isVisible = false;
    suchtext = '';

    /** Hinweis, der anstelle der leeren Liste angezeigt wird. */
    leerText = '';

    /** Zählt die Ladevorgänge. Übernommen wird nur die Antwort des zuletzt gestarteten. */
    private ladevorgang = 0;

    /** Suchtext, mit dem die Liste zuletzt angefordert wurde. Null, solange noch nichts geladen wurde. */
    private angeforderterSuchtext: string = null;

    constructor(
        private cdr: ChangeDetectorRef,
        private systemService: SystemService,
        private stammdatenService: StammdatenService,
        private remoteService: RemoteService,
        private modalController: ModalController,) {

        // todo
        AdresseSuchenComponent.instance = this;
    }

    ngOnChanges(changes: SimpleChanges) {
        log?.debug('ngOnChanges');
    }

    ngOnDestroy(): void {
        log?.debug('ngOnDestroy');
        this.isVisible = false;
        MyApp.loading(false);
    }

    async ngOnInit() {
        log?.debug('ngOnInit');

        this.isVisible = true;
        this.sichtbareItems = [];

        if (!this.items) {
            // Wenn es hier keinen Delay gibt, dann gibt es komischerweise eine Exception beim Anzeigen des Loading-Indicators:
            // NG0100: Expression has changed after it was checked
            await Utils.delay(100);

            await this.ladeAdressen(false);
        }

        for (const item of this.items) {
            if (item.adresse) {
                let adresse = item.adresse.AdressNummer;

                // item.text = adresse.Name1;
                // item.subtext = item.vertrag.Vertragsnummer;

                // if (item.adresse.Zusatzdaten?.BestellungVorhanden === '1') {
                //     item.istFavorit = true;
                // }

                item.suchtext = item.adresse.Name1
                    + ';' + item.adresse.Name2
                    + ';' + item.adresse.Strasse
                    + ';' + item.adresse.PLZ
                    + ';' + item.adresse.Ort;

            } else {
                item.text = '???';
                item.subtext = '';
                item.suchtext = '';
            }

            item.suchtext = item.suchtext.toLowerCase();
        }

        this.refresh();
    }

    async doRefresh(event) {
        log?.debug('doRefresh');

        await UiHelper.showLoading('Adressen werden geladen...');

        event.target.complete();

        try {
            if (await this.ladeAdressen(true)) {
                this.refresh();
            }
        } catch (err) {
            UiHelper.showError(err);
        } finally {
            UiHelper.hideLoading();
        }
    }

    /**
     * Lädt die Adressen vom ERP-System. Ohne Suchtext ist das die Startliste, die das ERP-System
     * auf den Umkreis beschränken kann.
     * @returns false, wenn die Antwort verworfen wurde, weil inzwischen neu gesucht wurde
     */
    async ladeAdressen(force: boolean, suchtext = this.getSuchtext()): Promise<boolean> {
        log?.debug('ladeAdressen');

        const ladevorgang = ++this.ladevorgang;
        this.angeforderterSuchtext = suchtext;

        this.items = [];
        this.cdr.detectChanges();

        let adressen: Adresse[] = [];

        MyApp.loading(true);

        // Position wird für den Umkreis-Filter benötigt. Ob gefiltert wird, entscheidet das ERP-System.
        let position = await this.systemService.getAktuelleGpsPosition('Adressen suchen', 60);

        if (!position) {
            // Der erste Versuch scheitert oft an einem kalten GPS (und im Browser fast immer).
            position = await this.systemService.getAktuelleGpsPosition('Adressen suchen (2. Versuch)', 60)
                || this.systemService.letzteGpsPosition;
        }

        const result = await this.remoteService.sucheAdressen({
            Suchtext: suchtext,
            Position: position
        });

        if (!this.isVisible) {
            return false;
        }

        if (ladevorgang !== this.ladevorgang) {
            // Inzwischen wurde neu gesucht, diese Antwort ist veraltet.
            // Die Ladeanzeige schaltet die neuere Anfrage aus.
            return false;
        }

        MyApp.loading(false);

        let fehlertext = '';

        if (result?.Success && result.Adressen) {
            adressen = result.Adressen;
            AdresseSuchenComponent.adresseCache = adressen;

            if (result.ErrorMessage) {
                // Hinweis des ERP-Systems, die Adressen gelten trotzdem
                // (z.B. Umkreis-Filter mangels GPS-Position nicht angewendet)
                UiHelper.showError(result.ErrorMessage);
            }
        } else if (result?.ErrorMessage) {
            fehlertext = result.ErrorMessage;
            UiHelper.showError(result.ErrorMessage);
        } else {
            UiHelper.showFehlerKommunikation();
        }

        // Liefert das ERP-System Entfernungen, ist der Umkreis-Filter aktiv
        if (adressen.some(p => p.Entfernung !== null && p.Entfernung !== undefined)) {
            AdresseSuchenComponent.geoFilterAktiv = true;
        }

        this.items = [];

        for (const adresse of adressen) {
            const item: AdresseListItem = {
                adresse: adresse,
                entfernungText: AdresseSuchenComponent.getEntfernungText(adresse.Entfernung)
            }

            this.items.push(item);
        }

        if (this.items.length) {
            this.leerText = '';
        } else if (fehlertext) {
            this.leerText = fehlertext;
        } else if (AdresseSuchenComponent.geoFilterAktiv && !suchtext) {
            // Nur die Startliste ist auf den Umkreis beschränkt, die Suche läuft über alle Adressen
            this.leerText = 'Keine Adressen in der Nähe gefunden';
        } else {
            this.leerText = 'Keine Adressen gefunden';
        }

        log?.debug('ladeAdressen done');

        this.cdr.detectChanges();

        return true;
    }

    /**
     * Formatiert die vom ERP-System gelieferte Luftlinie (in Metern) für die Liste.
     */
    private static getEntfernungText(meter: number): string {
        if (meter === null || meter === undefined) {
            return null;
        }

        if (meter < 1000) {
            return Math.round(meter) + ' m';
        }

        return (meter / 1000).toFixed(1).replace('.', ',') + ' km';
    }

    async ngAfterViewInit() {
        log?.debug('ngAfterViewInit');

        // setTimeout(() => {
        //     if (this.searchbar) {
        //         try {
        //             this.searchbar.setFocus();
        //         } catch (err) {
        //             log.error('setTimeout onSuchen: ' + Utils.getErrorMessage(err), err);
        //         }
        //     }

        //     this.cdr.detectChanges();
        // }, 500);

        // this.cdr.detectChanges();
    }

    refresh() {
        log?.debug('refresh');
        this.filterSichtbareItems();
    }

    onClose() {
        log?.debug('onClose');
        this.modalController.dismiss(null);
    }

    /** Enter oder Verlassen des Suchfelds */
    async onSearchChanged() {
        log?.debug('onSearchChanged');

        await this.sucheStarten(this.getSuchtext());
    }

    /**
     * Eingabe im Suchfeld, von Ionic entprellt (debounce). Gesucht wird erst mit Enter bzw. beim Verlassen
     * des Feldes. Wurde das Feld geleert, erscheint aber gleich wieder die Startliste.
     */
    async onSearchInput() {
        if (!this.getSuchtext()) {
            await this.sucheStarten('');
        }
    }

    /**
     * X-Button im Suchfeld. Ionic leert das Feld erst kurz nach diesem Event, deshalb wird hier
     * direkt die Startliste angefordert.
     */
    async onSearchCleared() {
        log?.debug('onSearchCleared');

        await this.sucheStarten('');
    }

    private async sucheStarten(suchtext: string) {
        if (suchtext === this.angeforderterSuchtext) {
            // Diese Liste wird bereits angezeigt oder gerade geladen
            return;
        }

        if (await this.ladeAdressen(true, suchtext)) {
            this.filterSichtbareItems();
        }
    }

    /**
     * Aktueller Text im Suchfeld.
     * suchtext (ngModel) wird bei ion-searchbar nur über das entprellte ionInput aktualisiert, ionChange
     * (Enter, Verlassen des Feldes) kommt dagegen sofort. Wer innerhalb der Entprellzeit Enter drückt,
     * hätte sonst mit dem vorherigen Text gesucht.
     */
    private getSuchtext(): string {
        return (this.searchbar?.value ?? this.suchtext ?? '').trim();
    }

    private filterSichtbareItems() {
        log?.debug('filterSichtbareItems');

        // let sichtbareItems: VertragListItem[];

        // if (this.suchtext && this.suchtext.trim()) {
        //     sichtbareItems = [];

        //     const parts: string[] = this.suchtext.toLowerCase().trim().split(' ');

        //     for (const item of this.items) {
        //         let isMatch = true;

        //         for (const str of parts) {
        //             if (!item.suchtext.includes(str)) {
        //                 isMatch = false;
        //                 break;
        //             }
        //         }

        //         if (isMatch) {
        //             sichtbareItems.push(item);
        //         }
        //     }
        // } else {
        //     sichtbareItems = this.items;
        // }

        // this.sichtbareItems = sichtbareItems;

        // Die Suche läuft derzeit über den Server
        this.sichtbareItems = this.items;

        this.cdr.detectChanges();
    }

    async onItemSelected(item: AdresseListItem) {
        log?.debug('onItemSelected', item);

        const modal = await this.modalController.create({
            component: AdresseDetailsComponent,
            componentProps: {
                adresse: item.adresse,
                auftragstyp: this.auftragstyp
            }
        });

        await modal.present();

        const result = await modal.onDidDismiss();

        if (result && result.data) {
            setTimeout(() => {
                this.modalController.dismiss(result.data);
            });
        }
    }
}
